/**
 * Course type keys for GuardRails flows and plugin mapping (Bronte Viewer, ePub, other).
 * @module services/GuardRailsService/GuardRailsCourseTypes
 */

/** Course type string constants for GuardRails selection and plugin mapping. */
const GUARDRAILS_COURSE_TYPES = {
  NONE: 'NONE',
  BV: 'BV',
  EPUB: 'EPUB',
};

/** Maps {@link GUARDRAILS_COURSE_TYPES} values to display name and known plugin id for course detection. */
const GuardrailPluginMap = {
  [GUARDRAILS_COURSE_TYPES.BV]: {
    name: "Bronte Viewer eText Plugin",
    id: '291c4490-4981-11ec-8f92-0b8eda21ab42',
  },
  [GUARDRAILS_COURSE_TYPES.EPUB]: {
    name: "Pearson eText course plugin",
    id: '1dac9300-416a-11eb-ba59-155b4a827473',
  },
};

/**
 * Resolves GuardRails course type from a course plugin reference.
 * @param {{pluginId?: string, name?: string}} plugin
 * @returns {string} A {@link GUARDRAILS_COURSE_TYPES} value, or OTHER when unmatched.
 */
const getCourseTypeFromPlugin = (plugin) => {
  const { pluginId, name } = plugin || {};

  for (const [courseType, entry] of Object.entries(GuardrailPluginMap)) {
    if (name === entry.name || pluginId === entry.id)  {
      return courseType;
    }
  }
  return GUARDRAILS_COURSE_TYPES.NONE;
};

/** Dropdown options for GuardRails course type selection UI. */
const GUARDRAILS_COURSE_TYPE_OPTIONS = [
  GUARDRAILS_COURSE_TYPES.BV,
  GUARDRAILS_COURSE_TYPES.EPUB,
  GUARDRAILS_COURSE_TYPES.NONE,
];

/** Course types that cannot be selected when another type is already chosen. */
const EXCLUSIVE_COURSE_TYPE_PAIRS = {
  [GUARDRAILS_COURSE_TYPES.BV]: GUARDRAILS_COURSE_TYPES.EPUB,
  [GUARDRAILS_COURSE_TYPES.EPUB]: GUARDRAILS_COURSE_TYPES.BV,
};

/**
 * Returns course type options available for the current selection.
 * BV and EPUB are mutually exclusive once one is selected, or inferred from the course plugin.
 * @param {string} selectedCourseType
 * @param {string} [coursePluginType=NONE] - Course type inferred from the activity plugin.
 * @returns {string[]}
 */
const getSelectableCourseTypeOptions = (
  selectedCourseType,
  coursePluginType = GUARDRAILS_COURSE_TYPES.NONE,
) => {
  const effectiveCourseType =
    selectedCourseType !== GUARDRAILS_COURSE_TYPES.NONE
      ? selectedCourseType
      : coursePluginType;

  // Courses authored for any other course types other than BV or EPUB will have only None option in the dropdown
  if (effectiveCourseType === GUARDRAILS_COURSE_TYPES.NONE) {
    return [GUARDRAILS_COURSE_TYPES.NONE];
  }

  const excludedCourseType = EXCLUSIVE_COURSE_TYPE_PAIRS[effectiveCourseType];

  return GUARDRAILS_COURSE_TYPE_OPTIONS.filter(
    (courseType) => courseType !== excludedCourseType,
  );
};

export {
  GuardrailPluginMap,
  GUARDRAILS_COURSE_TYPES,
  GUARDRAILS_COURSE_TYPE_OPTIONS,
  getCourseTypeFromPlugin,
  getSelectableCourseTypeOptions,
};